import { ReactNode, useEffect, useState } from 'react';
import GpanelLogo from '../../../../assets/gpanel.svg';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MultipleStop from '@mui/icons-material/MultipleStop';

export interface ConnectingDialogProps {
  open: boolean;
  onClose: () => void;
  integrationLogo?: ReactNode | string;
  integrationName?: ReactNode;
  progress?: number;
}

export default function ConnectingDialog({
  open,
  onClose,
  integrationLogo,
  integrationName,
  progress: externalProgress,
}: ConnectingDialogProps) {
  const [internalProgress, setInternalProgress] = useState(0);

  useEffect(() => {
    if (!open) {
      setInternalProgress(0);
      return;
    }
    if (externalProgress !== undefined) {
      return;
    }

    const timer = setInterval(() => {
      setInternalProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        return Math.min(prev + 10, 100);
      });
    }, 300);

    return () => {
      clearInterval(timer);
    };
  }, [open, externalProgress]);

  const progress =
    externalProgress !== undefined ? externalProgress : internalProgress;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          width: 400,
          height: 266,
          maxWidth: 400,
          maxHeight: 266,
          padding: '40px 24px 24px 24px',
          boxSizing: 'border-box',
          borderRadius: '8px',
        },
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="space-between"
        sx={{ height: '100%', width: '100%' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={3}
        >
          <Stack alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src={GpanelLogo}
                alt="gPanel"
                sx={{ width: 36, height: 36, objectFit: 'contain' }}
              />
            </Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, textAlign: 'center' }}
            >
              gPanel
            </Typography>
          </Stack>

          <MultipleStop
            sx={{
              fontSize: 28,
              color: (theme) => theme.palette.primary.main,
            }}
          />

          <Stack alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {typeof integrationLogo === 'string' ? (
                <Box
                  component="img"
                  src={integrationLogo}
                  alt={
                    typeof integrationName === 'string'
                      ? integrationName
                      : 'Integration'
                  }
                  sx={{ width: 48, height: 48, objectFit: 'contain' }}
                />
              ) : (
                integrationLogo
              )}
            </Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, textAlign: 'center' }}
            >
              {integrationName}
            </Typography>
          </Stack>
        </Stack>

        <Stack spacing={1} sx={{ width: '100%' }} alignItems="center">
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ width: '100%', height: 6, borderRadius: 3 }}
          />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {`${Math.round(progress)}%`}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mt: '16px !important' }}
          >
            {progress >= 100 ? (
              'Connected'
            ) : (
              <>Connecting to {integrationName}, please wait...</>
            )}
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
}
